import { supabase } from '@/lib/supabase'

export type AuditAction =
  | 'login'
  | 'logout'
  | 'create_turno'
  | 'update_turno'
  | 'delete_turno'
  | 'create_cliente'
  | 'update_cliente'
  | 'delete_cliente'
  | 'create_empleado'
  | 'update_empleado'
  | 'delete_empleado'
  | 'create_servicio'
  | 'update_servicio'
  | 'delete_servicio'
  | 'export_csv'
  | 'send_whatsapp'
  | 'change_plan'
  | 'invite_empleado'
  | 'accept_invitation'

export interface AuditLogEntry {
  action: AuditAction
  resource_id?: string
  resource_type?: string
  metadata?: Record<string, unknown>
  ip?: string
  user_agent?: string
}

/**
 * Registra una acción en el log de auditoría
 * Se ejecuta en background (fire-and-forget) para no bloquear la UI
 */
export async function auditLog(
  entry: AuditLogEntry,
  tenantId: string,
  userId?: string
) {
  if (!tenantId) return

  try {
    await supabase.from('audit_logs').insert({
      tenant_id: tenantId,
      user_id: userId || null,
      action: entry.action,
      resource_id: entry.resource_id || null,
      resource_type: entry.resource_type || null,
      metadata: entry.metadata || {},
      ip: entry.ip || null,
      user_agent: entry.user_agent || navigator.userAgent,
      created_at: new Date().toISOString(),
    })
  } catch (error) {
    // Silencioso - no bloquear la app si falla el log
    console.warn('[AuditLog] Error:', error)
  }
}

/**
 * Hook para usar auditLog en componentes
 */
export function useAuditLog() {
  return auditLog
}

/**
 * Helpers para acciones comunes
 */
export const audit = {
  login: (tenantId: string, userId?: string) => auditLog({ action: 'login' }, tenantId, userId),
  logout: (tenantId: string, userId?: string) => auditLog({ action: 'logout' }, tenantId, userId),

  turno: {
    create: (tenantId: string, userId: string | undefined, turnoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'create_turno', resource_id: turnoId, resource_type: 'turno', metadata }, tenantId, userId),
    update: (tenantId: string, userId: string | undefined, turnoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'update_turno', resource_id: turnoId, resource_type: 'turno', metadata }, tenantId, userId),
    delete: (tenantId: string, userId: string | undefined, turnoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'delete_turno', resource_id: turnoId, resource_type: 'turno', metadata }, tenantId, userId),
  },

  cliente: {
    create: (tenantId: string, userId: string | undefined, clienteId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'create_cliente', resource_id: clienteId, resource_type: 'cliente', metadata }, tenantId, userId),
    update: (tenantId: string, userId: string | undefined, clienteId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'update_cliente', resource_id: clienteId, resource_type: 'cliente', metadata }, tenantId, userId),
    delete: (tenantId: string, userId: string | undefined, clienteId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'delete_cliente', resource_id: clienteId, resource_type: 'cliente', metadata }, tenantId, userId),
  },

  empleado: {
    create: (tenantId: string, userId: string | undefined, empleadoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'create_empleado', resource_id: empleadoId, resource_type: 'empleado', metadata }, tenantId, userId),
    update: (tenantId: string, userId: string | undefined, empleadoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'update_empleado', resource_id: empleadoId, resource_type: 'empleado', metadata }, tenantId, userId),
    delete: (tenantId: string, userId: string | undefined, empleadoId: string, metadata?: Record<string, unknown>) =>
      auditLog({ action: 'delete_empleado', resource_id: empleadoId, resource_type: 'empleado', metadata }, tenantId, userId),
  },

  export: (tenantId: string, userId: string | undefined, tipo: string, count: number) =>
    auditLog({ action: 'export_csv', metadata: { tipo, count } }, tenantId, userId),

  whatsapp: (tenantId: string, userId: string | undefined, tipo: string, count: number) =>
    auditLog({ action: 'send_whatsapp', metadata: { tipo, count } }, tenantId, userId),
}